import {
  appendOp as coreAppendOp,
  bytesToHex,
  chunkForQr,
  encodeBundle,
  decodeBundle,
  epochAt,
  fromB64Url,
  generateGroupKey,
  generateIdentity,
  ingestBundle,
  isHex64,
  makeBundle,
  memberSetAt,
  OpStore,
  planRotation,
  unwrapRotation,
  publicKeyForPrivateKey,
  QR_FRAME_PREFIX_V2,
  randomBytes,
  reduce,
  toB64Url,
  type Bundle,
  type Envelope,
  type ExpenseBody,
  type GroupState,
  type Identity,
  type KeyRotateBody,
  type Op,
  type OpBody,
  type OpKind,
  type PaymentBody,
} from "@psplit/ledger-core";
import { db, type GroupMeta, type SyncState } from "./db.js";
import {
  decryptMsg,
  encryptMsg,
  encryptMsgToQrPayload,
  envelopeToQrPayload,
  isV2QrPayload,
  parseQrPayload,
  type WireMsg,
} from "./wire.js";

// Single source of truth for the UI. Owns the identity, group metadata, group
// keys and the wire protocol, and one OpStore per group; persists every
// accepted op to IndexedDB; notifies React via a version counter
// (useSyncExternalStore-friendly). State caching is per group: syncing one
// group never invalidates another group's reduced state.

export interface LoadWarning {
  groupId: string;
  detail: string;
}

export interface WireIngestOutcome {
  kind: WireMsg["t"];
  added: number;
  rejected: number;
  // hashes accepted FROM the wire this call; ops we authored in response (the
  // auto-admit member_add) are deliberately not listed, so sync can tell
  // "already on the relay" apart from "still needs pushing"
  acceptedHashes: string[];
  // set only for kind "req": the scanning device answers by showing a delta
  // ledger QR built against these heads
  reqHeads?: string[];
}

// v2 QR frames are denser than v1's 700 chars; modern phone cameras read them
// fine and each saved frame is cycle time saved.
const QR_CHUNK_V2 = 900;

class Repo {
  identity: Identity | null = null;
  profileName = "";
  profileVpa = "";
  seenIntro = false; // device-local: has the first-run walkthrough been shown
  ready = false;
  loadWarnings: LoadWarning[] = [];
  // Called after every locally authored op on a relay-backed group with
  // auto-sync on. App.tsx registers a debounced syncGroup here; repo cannot
  // import relaySync itself (relaySync imports repo).
  onLocalOp: ((groupId: string) => void) | null = null;

  private stores = new Map<string, OpStore>();
  private metas = new Map<string, GroupMeta>();
  private stateCache = new Map<string, { groupVersion: number; state: GroupState }>();
  private groupVersions = new Map<string, number>();
  private listeners = new Set<() => void>();
  private version = 0;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getVersion = (): number => this.version;

  private bump(groupId?: string) {
    this.version++;
    if (groupId !== undefined) this.groupVersions.set(groupId, this.version);
    for (const listener of this.listeners) listener();
  }

  async init(): Promise<void> {
    if (this.ready) return;
    const d = await db();
    // Device-local kv, read concurrently rather than in four serial round-trips.
    const [identity, profileName, profileVpa, seenIntro] = await Promise.all([
      d.get("kv", "identity"),
      d.get("kv", "profileName"),
      d.get("kv", "profileVpa"),
      d.get("kv", "seenIntro"),
    ]);
    this.identity = (identity as Identity | undefined) ?? null;
    this.profileName = (profileName as string | undefined) ?? "";
    this.profileVpa = (profileVpa as string | undefined) ?? "";
    // First-run intro shows only on a genuinely new device: no stored flag AND
    // no identity yet. An existing user (identity present) skips it, and the
    // proxy is stable, so removing the last group cannot resurface it.
    // markIntroSeen persists the flag once the user finishes or skips it.
    this.seenIntro = (seenIntro as boolean | undefined) ?? this.identity !== null;
    for (const meta of await d.getAll("groups")) {
      this.metas.set(meta.groupId, meta);
      const rows = await d.getAllFromIndex("ops", "byGroup", meta.groupId);
      const store = new OpStore();
      if (rows.length > 0) {
        const result = ingestBundle(store, {
          v: 1,
          group: meta.groupId,
          ops: rows.map((r) => r.op),
        });
        if (result.rejected.length > 0) {
          // Persisted ops failing re-validation means local corruption. Surface
          // it loudly; the group can be re-seeded from any other member.
          this.loadWarnings.push({
            groupId: meta.groupId,
            detail: `${result.rejected.length} stored ops failed validation and were dropped`,
          });
        }
      }
      this.stores.set(meta.groupId, store);
    }
    this.ready = true;
    this.bump();
  }

  async markIntroSeen(): Promise<void> {
    if (this.seenIntro) return;
    this.seenIntro = true;
    await (await db()).put("kv", true, "seenIntro");
    this.bump();
  }

  // ---- identity ----

  async setIdentity(name: string, vpa = ""): Promise<void> {
    if (this.identity) throw new Error("identity already exists");
    this.identity = generateIdentity();
    this.profileName = name;
    this.profileVpa = vpa;
    const d = await db();
    await d.put("kv", this.identity, "identity");
    await d.put("kv", name, "profileName");
    await d.put("kv", vpa, "profileVpa");
    this.bump();
  }

  exportIdentity(): string {
    if (!this.identity) throw new Error("no identity");
    return JSON.stringify({ ...this.identity, name: this.profileName }, null, 2);
  }

  async importIdentity(json: string): Promise<void> {
    const parsed = JSON.parse(json) as Identity & { name?: string };
    if (!isHex64(parsed.privKey)) {
      throw new Error("not an identity backup");
    }
    // Re-derive the public key from the private key instead of trusting the
    // file. A backup whose pubKey does not match would sign ops that fail their
    // own signature check, silently making the restored identity unable to write.
    const pubKey = publicKeyForPrivateKey(parsed.privKey);
    if (isHex64(parsed.pubKey) && parsed.pubKey !== pubKey) {
      throw new Error("identity backup is inconsistent: public key does not match the private key");
    }
    this.identity = { privKey: parsed.privKey, pubKey };
    this.profileName = parsed.name ?? this.profileName;
    const d = await db();
    await d.put("kv", this.identity, "identity");
    await d.put("kv", this.profileName, "profileName");
    this.bump();
  }

  // ---- groups ----

  groups(): GroupMeta[] {
    return [...this.metas.values()].sort((a, b) => b.joinedAt - a.joinedAt);
  }

  meta(groupId: string): GroupMeta | null {
    return this.metas.get(groupId) ?? null;
  }

  storeOf(groupId: string): OpStore {
    const store = this.stores.get(groupId);
    if (!store) throw new Error("unknown group");
    return store;
  }

  state(groupId: string): GroupState | null {
    const store = this.stores.get(groupId);
    if (!store || store.size === 0) return null;
    const groupVersion = this.groupVersions.get(groupId) ?? 0;
    const cached = this.stateCache.get(groupId);
    if (cached && cached.groupVersion === groupVersion) return cached.state;
    const state = reduce(store);
    this.stateCache.set(groupId, { groupVersion, state });
    return state;
  }

  membership(groupId: string): "member" | "pending" | "left" {
    const me = this.identity?.pubKey;
    const state = this.state(groupId);
    if (!me || !state) return "pending";
    const member = state.members.find((m) => m.pubKey === me);
    if (!member) return "pending";
    return member.active ? "member" : "left";
  }

  private async persistOps(groupId: string, ops: Op[]): Promise<void> {
    if (ops.length === 0) return;
    const d = await db();
    const tx = d.transaction("ops", "readwrite");
    for (const op of ops) void tx.store.put({ groupId, hash: op.hash, op });
    await tx.done;
  }

  private async appendOp(groupId: string, kind: OpKind, body: OpBody): Promise<Op> {
    if (!this.identity) throw new Error("no identity");
    const store = this.storeOf(groupId);
    const { op, result } = coreAppendOp(store, this.identity, kind, body, Date.now());
    if (!result.ok) throw new Error(result.reason);
    await this.persistOps(groupId, [op]);
    this.bump(groupId);
    const meta = this.metas.get(groupId);
    if (meta && meta.relayUrl !== "" && meta.autoSync !== false) this.onLocalOp?.(groupId);
    return op;
  }

  async createGroup(name: string, relayUrl: string): Promise<string> {
    if (!this.identity) throw new Error("no identity");
    const key = generateGroupKey();
    const store = new OpStore();
    const { op: genesis, result } = coreAppendOp(
      store,
      this.identity,
      "genesis",
      { name, currency: "INR" },
      Date.now(),
    );
    if (!result.ok) throw new Error(result.reason);
    const groupId = genesis.hash;
    const meta: GroupMeta = {
      groupId,
      name,
      keyB64: toB64Url(key),
      epoch: 0,
      pastKeys: [],
      relayUrl,
      joinedAt: Date.now(),
    };
    this.metas.set(groupId, meta);
    this.stores.set(groupId, store);
    await (await db()).put("groups", meta);
    await this.persistOps(groupId, [genesis]);
    if (this.profileName !== "" || this.profileVpa !== "") {
      await this.appendOp(groupId, "profile", { name: this.profileName, vpa: this.profileVpa });
    } else {
      this.bump(groupId);
    }
    return groupId;
  }

  async joinGroup(invite: {
    groupId: string;
    key: Uint8Array;
    epoch?: number;
    relayUrl: string;
    name: string;
  }): Promise<void> {
    if (this.metas.has(invite.groupId)) return;
    const meta: GroupMeta = {
      groupId: invite.groupId,
      name: invite.name === "" ? "New group" : invite.name,
      keyB64: toB64Url(invite.key),
      // An invite hands over the key that was current when it was made. Older
      // entries were written under keys this device will never hold, and that
      // is fine: history reaches it re-encrypted, inside a bundle from a member.
      epoch: invite.epoch ?? 0,
      pastKeys: [],
      relayUrl: invite.relayUrl,
      joinedAt: Date.now(),
    };
    this.metas.set(invite.groupId, meta);
    this.stores.set(invite.groupId, new OpStore());
    await (await db()).put("groups", meta);
    this.bump(invite.groupId);
  }

  async updateMeta(groupId: string, patch: Partial<Pick<GroupMeta, "name" | "relayUrl" | "autoSync">>): Promise<void> {
    const meta = this.metas.get(groupId);
    if (!meta) throw new Error("unknown group");
    const next = { ...meta, ...patch };
    this.metas.set(groupId, next);
    await (await db()).put("groups", next);
    this.bump(groupId);
  }

  // Forget a group on THIS device only: local ops, meta, sync cursor and
  // recurring templates. The ledger lives on the other members' devices; with
  // an invite link (or export file) this device can rejoin and re-pull.
  async removeGroup(groupId: string): Promise<void> {
    if (!this.metas.has(groupId)) throw new Error("unknown group");
    const d = await db();
    const tx = d.transaction(["groups", "ops", "syncstate", "recurring"], "readwrite");
    void tx.objectStore("groups").delete(groupId);
    void tx.objectStore("syncstate").delete(groupId);
    for (const key of await tx.objectStore("ops").index("byGroup").getAllKeys(groupId)) {
      void tx.objectStore("ops").delete(key);
    }
    for (const t of await tx.objectStore("recurring").index("byGroup").getAll(groupId)) {
      void tx.objectStore("recurring").delete(t.id);
    }
    await tx.done;
    this.metas.delete(groupId);
    this.stores.delete(groupId);
    this.stateCache.delete(groupId);
    this.groupVersions.delete(groupId);
    this.bump();
  }

  // ---- ledger actions ----

  addExpense(groupId: string, body: ExpenseBody): Promise<Op> {
    return this.appendOp(groupId, "expense", body);
  }

  addPayment(groupId: string, body: PaymentBody): Promise<Op> {
    return this.appendOp(groupId, "payment", body);
  }

  supersedeEntry(groupId: string, target: string, replacement: ExpenseBody | PaymentBody | null): Promise<Op> {
    return this.appendOp(groupId, "supersede", { target, replacement });
  }

  async addMember(groupId: string, member: string, name: string): Promise<Op> {
    return this.appendOp(groupId, "member_add", { member, name });
  }

  // A ghost is a person without the app: random id, tracked by name.
  async addGhost(groupId: string, name: string): Promise<Op> {
    const ghost = bytesToHex(randomBytes(32));
    return this.appendOp(groupId, "ghost_add", { ghost, name });
  }

  // Take over a ghost's history onto MY key (validation enforces self-claim).
  async claimGhost(groupId: string, ghost: string): Promise<Op> {
    if (!this.identity) throw new Error("no identity");
    return this.appendOp(groupId, "ghost_claim", { ghost, member: this.identity.pubKey });
  }

  async ackPayment(groupId: string, target: string): Promise<Op> {
    return this.appendOp(groupId, "payment_ack", { target });
  }

  async leaveGroup(groupId: string): Promise<void> {
    if (!this.identity) throw new Error("no identity");
    await this.appendOp(groupId, "member_leave", { member: this.identity.pubKey });
  }

  async setProfile(name: string, vpa: string): Promise<void> {
    this.profileName = name;
    this.profileVpa = vpa;
    const d = await db();
    await d.put("kv", name, "profileName");
    await d.put("kv", vpa, "profileVpa");
    for (const meta of this.metas.values()) {
      if (this.membership(meta.groupId) === "member") {
        await this.appendOp(meta.groupId, "profile", { name, vpa });
      }
    }
    this.bump();
  }

  // ---- wire protocol (repo owns the group key and envelope construction;
  //      the relay path and the QR path share these) ----

  groupKey(groupId: string): Uint8Array {
    const meta = this.metas.get(groupId);
    if (!meta) throw new Error("unknown group");
    return fromB64Url(meta.keyB64);
  }

  /** Which key epoch this device currently holds for a group. */
  epoch(groupId: string): number {
    return this.metas.get(groupId)?.epoch ?? 0;
  }

  /**
   * Every key this device knows for a group, newest first. Decryption walks
   * this: a replica may still be handed envelopes written under a key that has
   * since been rotated away, and dropping them would silently lose entries.
   */
  groupKeys(groupId: string): Uint8Array[] {
    const meta = this.metas.get(groupId);
    if (!meta) throw new Error("unknown group");
    const past = [...(meta.pastKeys ?? [])].sort((a, b) => b.epoch - a.epoch);
    return [fromB64Url(meta.keyB64), ...past.map((k) => fromB64Url(k.keyB64))];
  }

  /**
   * Adopt a key learned from a key_rotate op. Moving forward keeps the old key
   * as a past key; a key we already hold, or an older one, is filed and never
   * moves the group backwards.
   */
  async adoptKey(groupId: string, epoch: number, key: Uint8Array): Promise<boolean> {
    const meta = this.metas.get(groupId);
    if (!meta) throw new Error("unknown group");
    const keyB64 = toB64Url(key);
    const current = meta.epoch ?? 0;
    const past = [...(meta.pastKeys ?? [])];
    if (epoch <= current) {
      if (keyB64 === meta.keyB64 || past.some((k) => k.keyB64 === keyB64)) return false;
      past.push({ epoch, keyB64 });
      const filed = { ...meta, pastKeys: past };
      this.metas.set(groupId, filed);
      await (await db()).put("groups", filed);
      return false;
    }
    past.push({ epoch: current, keyB64: meta.keyB64 });
    const next: GroupMeta = { ...meta, keyB64, epoch, pastKeys: past };
    this.metas.set(groupId, next);
    await (await db()).put("groups", next);
    this.bump(groupId);
    return true;
  }

  /**
   * Unwrap any rotation addressed to us that we have not taken up yet. Called
   * after every ingest, because a rotation authored by someone else is how this
   * device normally learns the group changed its key.
   */
  async adoptRotations(groupId: string): Promise<boolean> {
    if (!this.identity) return false;
    let moved = false;
    for (const op of this.storeOf(groupId).topoOrder()) {
      if (op.kind !== "key_rotate") continue;
      const body = op.body as KeyRotateBody;
      if (body.epoch <= this.epoch(groupId) && this.knowsEpoch(groupId, body.epoch)) continue;
      const key = await unwrapRotation(body, this.identity);
      if (!key) continue; // not addressed to us: we were removed, or never in it
      moved = (await this.adoptKey(groupId, body.epoch, key)) || moved;
    }
    return moved;
  }

  private knowsEpoch(groupId: string, epoch: number): boolean {
    const meta = this.metas.get(groupId);
    if (!meta) return false;
    return (meta.epoch ?? 0) === epoch || (meta.pastKeys ?? []).some((k) => k.epoch === epoch);
  }

  /**
   * Change the group key, leaving out anyone no longer in the group. The op is
   * authored first and only adopted if it validates, so a rejected rotation
   * cannot strand this device on a key nobody else has.
   */
  async rotateKey(groupId: string): Promise<{ epoch: number; previousKey: Uint8Array }> {
    if (!this.identity) throw new Error("no identity");
    const store = this.storeOf(groupId);
    const heads = store.heads();
    if (!memberSetAt(store, heads).has(this.identity.pubKey)) {
      throw new Error("only a member can change the key");
    }
    const previousKey = this.groupKey(groupId);
    const plan = await planRotation(epochAt(store, heads) + 1, memberSetAt(store, heads));
    await this.appendOp(groupId, "key_rotate", plan.body);
    await this.adoptKey(groupId, plan.body.epoch, plan.groupKey);
    return { epoch: plan.body.epoch, previousKey };
  }

  async removeMember(groupId: string, member: string, reason: string): Promise<Op> {
    return this.appendOp(groupId, "member_remove", { member, reason });
  }

  encryptOpsEnvelope(groupId: string, ops: Op[]): Promise<Envelope> {
    return this.encryptOpsEnvelopeWith(this.groupKey(groupId), groupId, ops);
  }

  /**
   * Same, under a key of the caller's choosing. Used once, to post a rotation
   * back to the mailbox the group is leaving, so a member who was offline can
   * still find out they were rotated forward. Everything in that bundle was
   * already readable with that key, so it hands nothing new to a stale holder.
   */
  encryptOpsEnvelopeWith(key: Uint8Array, groupId: string, ops: Op[]): Promise<Envelope> {
    const store = this.storeOf(groupId);
    return encryptMsg(key, { t: "ops", b: { v: 1, group: store.groupId, ops } });
  }

  buildHelloEnvelope(groupId: string): Promise<Envelope> {
    if (!this.identity) throw new Error("no identity");
    return encryptMsg(this.groupKey(groupId), {
      t: "hello",
      pub: this.identity.pubKey,
      name: this.profileName,
    });
  }

  async ingestEnvelope(groupId: string, envelope: Envelope): Promise<WireIngestOutcome> {
    let msg: WireMsg | null = null;
    let lastError: unknown;
    // A rotation moves the mailbox, but envelopes written just before it can
    // still be in flight, so try every key this device holds before giving up.
    for (const key of this.groupKeys(groupId)) {
      try {
        msg = await decryptMsg(key, envelope);
        break;
      } catch (e) {
        lastError = e;
      }
    }
    if (msg === null) throw lastError instanceof Error ? lastError : new Error("cannot decrypt");
    const outcome = await this.ingestWire(groupId, msg);
    await this.adoptRotations(groupId);
    return { kind: msg.t, ...outcome };
  }

  // With remoteHeads (from a scanned "req"), only the ops the other device is
  // missing go into frames - the everyday face-to-face sync is 1-2 frames, not
  // the whole history.
  async buildLedgerQrFrames(groupId: string, remoteHeads: string[] = []): Promise<string[]> {
    const bundle = makeBundle(this.storeOf(groupId), remoteHeads);
    const payload = await encryptMsgToQrPayload(this.groupKey(groupId), { t: "ops", b: bundle });
    return chunkForQr(payload, QR_CHUNK_V2, QR_FRAME_PREFIX_V2);
  }

  // Tiny "here is what I already have" code; always a single frame.
  async buildSyncRequestQrFrames(groupId: string): Promise<string[]> {
    const heads = this.storeOf(groupId).heads().slice(0, 128);
    const payload = await encryptMsgToQrPayload(this.groupKey(groupId), { t: "req", heads });
    return chunkForQr(payload, QR_CHUNK_V2, QR_FRAME_PREFIX_V2);
  }

  // Hellos stay on the v1 encoding: they are one small frame anyway, and a
  // freshly installed (newest) app must be scannable by members who have not
  // reopened theirs since this codec shipped.
  async buildHelloQrFrames(groupId: string): Promise<string[]> {
    return chunkForQr(envelopeToQrPayload(await this.buildHelloEnvelope(groupId)));
  }

  async ingestQrPayload(groupId: string, payload: string): Promise<WireIngestOutcome> {
    const { envelope, compressed } = parseQrPayload(payload);
    const msg = await decryptMsg(this.groupKey(groupId), envelope, compressed);
    if (msg.t === "req") {
      return { kind: "req", added: 0, rejected: 0, acceptedHashes: [], reqHeads: msg.heads };
    }
    return { kind: msg.t, ...(await this.ingestWire(groupId, msg)) };
  }

  // ---- ingest from any transport ----

  async ingestWire(
    groupId: string,
    msg: WireMsg,
  ): Promise<{ added: number; rejected: number; acceptedHashes: string[] }> {
    const store = this.storeOf(groupId);
    // "req" never writes anything; ingestQrPayload answers it with a delta QR.
    // Reaching here means one arrived over the relay - ignore it.
    if (msg.t === "req") return { added: 0, rejected: 0, acceptedHashes: [] };
    if (msg.t === "hello") {
      // Possession of the group key = invited. Any active member auto-admits.
      // The member_add is authored LOCALLY, so acceptedHashes stays empty and
      // sync will push it.
      const me = this.identity?.pubKey;
      if (!me || this.membership(groupId) !== "member") return { added: 0, rejected: 0, acceptedHashes: [] };
      const activeNow = memberSetAt(store, store.heads());
      if (activeNow.has(msg.pub)) return { added: 0, rejected: 0, acceptedHashes: [] };
      await this.addMember(groupId, msg.pub, msg.name);
      return { added: 1, rejected: 0, acceptedHashes: [] };
    }
    const result = ingestBundle(store, msg.b);
    if (result.added > 0) {
      await this.persistOps(groupId, result.ops);
      await this.afterStoreChanged(groupId);
      this.bump(groupId);
    }
    return {
      added: result.added,
      rejected: result.rejected.length,
      acceptedHashes: result.ops.map((op) => op.hash),
    };
  }

  // After remote ops land: if we just became a member and have a profile name
  // that the group does not know yet, announce it once.
  private async afterStoreChanged(groupId: string): Promise<void> {
    const me = this.identity?.pubKey;
    if (!me || this.profileName === "") return;
    const store = this.storeOf(groupId);
    if (store.size === 0) return;
    const fresh = reduce(store);
    const mine = fresh.members.find((m) => m.pubKey === me);
    if (mine && mine.active && mine.name === "") {
      await this.appendOp(groupId, "profile", { name: this.profileName, vpa: this.profileVpa });
    }
  }

  // ---- file export/import ----

  exportBundleText(groupId: string, remoteHeads: string[] = []): string {
    return encodeBundle(makeBundle(this.storeOf(groupId), remoteHeads));
  }

  // Encrypted one-liner of the full ledger - safe to send through any
  // messaging app; only group members hold the key that opens it.
  exportSyncText(groupId: string): Promise<string> {
    const bundle = makeBundle(this.storeOf(groupId));
    return encryptMsgToQrPayload(this.groupKey(groupId), { t: "ops", b: bundle });
  }

  // Accepts both the plaintext backup format and the encrypted sync text.
  async importBundleText(groupId: string, text: string): Promise<{ added: number; rejected: number }> {
    const trimmed = text.trim();
    if (isV2QrPayload(trimmed)) {
      const outcome = await this.ingestQrPayload(groupId, trimmed);
      return { added: outcome.added, rejected: outcome.rejected };
    }
    return this.ingestWire(groupId, { t: "ops", b: decodeBundle(trimmed) as Bundle });
  }

  // ---- sync state ----

  async loadSyncState(groupId: string): Promise<SyncState> {
    const st = await (await db()).get("syncstate", groupId);
    return st ?? { groupId, cursor: 0, lastPushHeads: [], lastSyncAt: 0 };
  }

  async saveSyncState(st: SyncState): Promise<void> {
    // No bump: nothing renders sync-state reactively, and bumping here would
    // needlessly re-render (and previously invalidated every group's cache).
    await (await db()).put("syncstate", st);
  }
}

export const repo = new Repo();
